import {
  type ApiKeyId,
  apiKeyIdSchema,
  generateId,
  type OrganizationId,
  organizationIdSchema,
  type ProjectId,
  projectIdSchema,
} from "@domain/shared"
import { z } from "zod"

/**
 * API Key entity - authenticates organization-bound requests.
 *
 * API keys are scoped to an organization and provide token-based
 * authentication for API access. Tokens are UUIDs generated
 * using crypto.randomUUID().
 *
 * The token is encrypted at the application level (AES-256-GCM)
 * and a SHA-256 hex hash of the UTF-8 token bytes (tokenHash) is stored for indexed
 * lookups without decryption.
 */
export const apiKeySchema = z.object({
  id: apiKeyIdSchema,
  organizationId: organizationIdSchema,
  /**
   * When set, the key may only act on this project. `null` is an org-wide key
   * (every key that existed before project scope).
   */
  projectId: projectIdSchema.nullable(),
  token: z.string().min(1),
  tokenHash: z.string().min(1),
  name: z.string().min(1),
  lastUsedAt: z.date().nullable(),
  deletedAt: z.date().nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
})

export type ApiKey = z.infer<typeof apiKeySchema>

/**
 * Factory function to create a new API Key.
 */
export const createApiKey = (params: {
  id?: ApiKeyId | undefined
  organizationId: OrganizationId
  /** `null` (default) mints an org-wide key. */
  projectId?: ProjectId | null
  token: string
  tokenHash: string
  name: string
  lastUsedAt?: Date | null
  deletedAt?: Date | null
  createdAt?: Date
  updatedAt?: Date
}): ApiKey => {
  const now = new Date()
  return apiKeySchema.parse({
    id: params.id ?? generateId<"ApiKeyId">(),
    organizationId: params.organizationId,
    projectId: params.projectId ?? null,
    token: params.token,
    tokenHash: params.tokenHash,
    name: params.name,
    lastUsedAt: params.lastUsedAt ?? null,
    deletedAt: params.deletedAt ?? null,
    createdAt: params.createdAt ?? now,
    updatedAt: params.updatedAt ?? now,
  })
}

/**
 * Generate a new API key token (UUID v4), optionally prefixed.
 *
 * The optional `prefix` is prepended verbatim (e.g. `lat_sandbox_` for sandbox
 * orgs); the random UUID body is unchanged so lookups by `tokenHash` work the
 * same for prefixed and unprefixed tokens. Live keys pass no prefix.
 */
export const generateApiKeyToken = (prefix = ""): string => {
  return `${prefix}${crypto.randomUUID()}`
}

/**
 * Check if an API key is active (not soft-deleted).
 */
export const isActive = (apiKey: ApiKey): boolean => {
  return apiKey.deletedAt === null
}

/**
 * Touch the API key to update its lastUsedAt timestamp.
 */
export const touch = (apiKey: ApiKey): ApiKey => {
  const now = new Date()
  return apiKeySchema.parse({
    ...apiKey,
    lastUsedAt: now,
    updatedAt: now,
  })
}

/**
 * Revoke (soft delete) an API key.
 */
export const revoke = (apiKey: ApiKey): ApiKey => {
  const now = new Date()
  return apiKeySchema.parse({
    ...apiKey,
    deletedAt: now,
    updatedAt: now,
  })
}
